import { defineStore } from 'pinia';
import { ref, shallowRef, computed } from 'vue';
import { PROVINCES, STATES, validateEmail, validatePhone } from '@/common/helpers';
import { SITE_ASSESSMENT_KEYS } from '@/common/constants/site-assessment-groups';

const buildInitialSiteAssessment = () =>
  Object.fromEntries(SITE_ASSESSMENT_KEYS.map(key => [key, false]));

export const useSchoolApplicationStore = defineStore('schoolApplication', () => {
  const school = ref({
    name: null,
    street_address_1: null,
    street_address_2: null,
    city: null,
    program_type: 'ACE Service',
    state: null,
    zip_code: null,
    phone: null,
    country: 'USA',
    lms_type: null,
    status: 'Pending L1 Review',
    comments: '',
    donation_only: false,
    region_id: null,
    site_assessment: buildInitialSiteAssessment(),
    contacts_attributes: [
      {
        name: null,
        email: null,
        emailConfirm: null,
        phone: null,
        comments: null,
        category: null,
        is_primary: true,
        is_secondary: false,
        legal_signatory: null,
        contactable_type: 'School',
      },
      {
        name: null,
        email: null,
        emailConfirm: null,
        phone: null,
        comments: null,
        category: null,
        is_primary: false,
        is_secondary: true,
        legal_signatory: null,
        contactable_type: 'School',
      },
    ],
  });

  const selectedSchoolSponsoredDealers = ref([]);
  const selectedDealers = ref([]);
  const deletedDealerContacts = ref([]);
  const sponsoredSelected = ref(false);
  const provinces = shallowRef(PROVINCES);
  const states = shallowRef(STATES);
  const validationErrors = ref({});

  const validationAddressErrors = computed(() => {
    const keys = ['lmsType', 'streetAddress', 'state', 'city', 'programType', 'phoneNumber', 'zipCode'];
    return keys.reduce((prev, current) => (validationErrors.value?.[current]) ? ++prev : prev, 0);
  });

  const validationContactErrors = computed(() => {
    const keys = ['name', 'category', 'email', 'emailConfirm', 'phoneNumber'];

    let errorCount = keys
      .reduce((prev, current) => {
        const errorsInContact = validationErrors.value?.contacts?.reduce((innerPrev, currentContact) => {
          return currentContact?.[current] ? ++innerPrev : innerPrev;
        }, 0) ?? 0;

        return prev + errorsInContact;
      }, 0);

    if (validationErrors.value.primaryContact) {
      errorCount++;
    }

    return errorCount;
  });

  const assessmentComplete = computed(() =>
    SITE_ASSESSMENT_KEYS.every(key => !!school.value.site_assessment?.[key])
  );

  const validationDealerContactErrors = computed(() => {
    if (!sponsoredSelected.value) {
      const keys = ['name', 'category', 'email', 'phoneNumber'];

      return keys.reduce((prev, current) => {
        const errorsInContact = validationErrors.value.dealerContacts?.reduce((innerPrev, currentContact) => {
          return currentContact?.[current] ? ++innerPrev : innerPrev;
        }, 0) ?? 0;

        return prev + errorsInContact;
      }, 0);

    }

    return null;
  });

  function validateContact(contact) {
    const errorDict = {};

    const validContactName = !!contact.name;
    const validContactCategory = !!contact.category;
    const validEmail = !!contact.email;
    const validPhoneNumber = !!contact.phone;

    if (!validContactName) {
      errorDict.name = { message: 'Field required' };
    }

    if (!validContactCategory) {
      errorDict.category = { message: 'Field required' };
    }

    // Regular Email
    if (!validEmail) {
      errorDict.email = { message: 'Field required' };
    } else {
      // if it is valid, then let's validate email too.
      if (!validateEmail(contact.email)) {
        errorDict.email = { message: 'Not a valid email' };
      }
    }

    // if there's an email validation in part of it, let's validate it
    if ('emailConfirm' in contact && (!contact.emailConfirm || contact.emailConfirm !== contact.email)) {
      errorDict.emailConfirm = { message: !contact.emailConfirm ? 'Field required' : 'Not the same email' };
    }

    if (!validPhoneNumber) {
      errorDict.phoneNumber = { message: 'Field required' };
    } else {
      if (!validatePhone(contact.phone)) {
        errorDict.phoneNumber = { message: 'Not a valid phone number' };
      }
    }

    return errorDict;
  }

  function validateSchoolAddress() {
    // The !! checks for truthy value, essentially checking if they've been filled in.
    const validLmsType = !!school.value.lms_type;
    const validStreetAddress = !!school.value.street_address_1;
    const validCity = !!school.value.city;
    const validState = !!school.value.state;
    const validProgramType = !!school.value.program_type;
    const validZipCode = !!school.value.zip_code;
    const validPhoneNumber = !!school.value.phone;

    // Reset its value
    validationErrors.value = {};

    if (!validLmsType) {
      validationErrors.value.lmsType = {
        message: 'Field is required',
      };
    }

    if (!validStreetAddress) {
      validationErrors.value.streetAddress = {
        message: 'Field is required',
      };
    }

    if (!validCity) {
      validationErrors.value.city = {
        message: 'Field is required',
      };
    }

    if (!validState) {
      validationErrors.value.state = {
        message: 'Field is required',
      };
    }

    if (!validProgramType) {
      validationErrors.value.programType = {
        message: 'Field is required',
      };
    }

    if (!validZipCode) {
      validationErrors.value.zipCode = {
        message: 'Field is required',
      };
    }

    if (!validPhoneNumber) {
      validationErrors.value.phoneNumber = {
        message: 'Field is required',
      };
    }
  }

  function validateDealerContacts() {
    validationErrors.value.dealerContacts = selectedSchoolSponsoredDealers.value
      .flatMap(dealer => dealer?.contacts_attributes ?? [])
      .map(contact => validateContact(contact));
  }

  function validateSchoolContacts() {
    validationErrors.value.contacts = school.value.contacts_attributes
      .map((contact) => validateContact(contact));

    // This checks if there's at least 1 primary contact
    const hasOnePrimaryContact = school.value.contacts_attributes.some(contact => contact.is_primary);

    if (!hasOnePrimaryContact) {
      validationErrors.value.primaryContact = { message: 'Primary Contact selection required' };
    }
  }

  // attempts to validate the school on the frontend side.
  function validateSchool(addProgramType = false) {
    validateSchoolAddress();
    validateSchoolContacts();

    if (school.value.parent_id) {
      return validationAddressErrors.value === 0;

    } else if (!addProgramType) {
      validateDealerContacts();

      return validationAddressErrors.value === 0 && validationContactErrors.value === 0 && validationDealerContactErrors.value === 0;
    }

    return validationAddressErrors.value === 0 && validationContactErrors.value === 0;
  }

  function resetSchool() {
    school.value = {
      name: null,
      street_address_1: null,
      street_address_2: null,
      city: null,
      program_type: 'ACE Service',
      state: null,
      zip_code: null,
      phone: null,
      country: 'USA',
      lms_type: null,
      status: 'Pending L1 Review',
      comments: '',
      donation_only: false,
      region_id: null,
      site_assessment: buildInitialSiteAssessment(),
      contacts_attributes: [
        {
          name: null,
          email: null,
          emailConfirm: null,
          phone: null,
          comments: null,
          category: null,
          is_primary: true,
          is_secondary: false,
          legal_signatory: null,
          contactable_type: 'School',
        },
        {
          name: null,
          email: null,
          emailConfirm: null,
          phone: null,
          comments: null,
          category: null,
          is_primary: false,
          is_secondary: true,
          legal_signatory: null,
          contactable_type: 'School',
        },
      ],
    };
    selectedSchoolSponsoredDealers.value = [];
    selectedDealers.value = [];
    deletedDealerContacts.value = [];
    sponsoredSelected.value = false;
    validationErrors.value = {};
  }

  function setSchool(newSchool) {
    school.value = newSchool;
  }

  return {
    school,
    provinces,
    states,
    sponsoredSelected,
    selectedDealers,
    selectedSchoolSponsoredDealers,
    deletedDealerContacts,
    assessmentComplete,
    validationAddressErrors,
    validationContactErrors,
    validationDealerContactErrors,
    validationErrors,
    validateSchool,
    resetSchool,
    setSchool,
  };
})
